import React from "react";
import { perjanjianInternasionalAgreements } from "./data/perjanjianInternasionalData";

const PerjanjianInternasional: React.FC = () => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "masih berlaku":
        return "badge-success";
      case "proses perpanjangan":
      case "proses perjanjian":
        return "badge-warning";
      case "habis masa berlaku":
      case "diberhentikan":
        return "badge-error";
      case "siap ditandatangani":
        return "badge-info";
      case "beragam status":
        return "badge-neutral";
      default:
        return "badge-neutral";
    }
  };

  return (
    <div>
      <div className="mx-auto w-11/12 laptop:max-w-full desktop:max-w-7xl">
        <div className="flex flex-row my-12">
          <div className="p-4 w-full">
            <div className="card w-full bg-base-100 shadow-xl border-t-8 border-blue-600 rounded-xl">
              <div className="card-body p-0">
                <div className="flex flex-row px-6 pt-6">
                  <div className="grow">
                    <h2 className="card-title text-3xl font-bold text-base-content">
                      Perjanjian Internasional
                    </h2>
                    <p className="text-base-content/70 mt-2">
                      Perjanjian Kerjasama Internasional Kepolisian Republik
                      Indonesia
                    </p>
                  </div>
                  <div className="shrink">
                    <div className="text-sm breadcrumbs">
                      <ul>
                        <li>
                          <a
                            href="/"
                            className="text-base-content/60 hover:text-base-content"
                          >
                            Beranda
                          </a>
                        </li>
                        <li className="text-base-content/60">
                          Kerja Sama Internasional
                        </li>
                        <li className="text-primary font-semibold">
                          Perjanjian Internasional
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="divider m-0"></div>

                <div className="p-6">
                  {/* Introduction */}
                  <div className="bg-base-200 rounded-xl p-6 mb-8">
                    <h3 className="text-xl font-bold text-base-content mb-4">
                      Tentang Perjanjian Internasional
                    </h3>
                    <p className="text-base-content/70 leading-relaxed">
                      Perjanjian Internasional merupakan instrumen hukum yang
                      mengikat antara Kepolisian Republik Indonesia dengan
                      negara-negara mitra dalam rangka kerjasama pencegahan dan
                      pemberantasan kejahatan transnasional. Perjanjian ini
                      mencakup berbagai bentuk kerjasama seperti pertukaran
                      informasi, pelatihan, pengembangan kapasitas, dan operasi
                      bersama untuk memerangi kejahatan lintas negara.
                    </p>
                  </div>

                  {/* Agreements by Continent */}
                  <div className="space-y-8">
                    {perjanjianInternasionalAgreements.map((continent) => (
                      <div
                        key={continent.name}
                        className="bg-base-200 rounded-xl p-6"
                      >
                        <h3 className="text-2xl font-bold text-primary mb-6 border-b border-base-300 pb-3">
                          {continent.name}
                        </h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {continent.countries.map((agreement, index) => (
                            <div
                              key={index}
                              className="bg-base-100 rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow"
                            >
                              <div className="flex justify-between items-start mb-3">
                                <h4 className="text-lg font-bold text-base-content">
                                  {agreement.country}
                                </h4>
                                <span
                                  className={`badge ${getStatusColor(agreement.status)} badge-sm`}
                                >
                                  {agreement.status}
                                </span>
                              </div>

                              <div className="space-y-3">
                                <div>
                                  <p className="text-sm font-semibold text-base-content/80 mb-2">
                                    Judul Perjanjian:
                                  </p>
                                  <p className="text-sm text-base-content/70 leading-relaxed">
                                    {agreement.title}
                                  </p>
                                </div>

                                {agreement.organizations && (
                                  <div>
                                    <p className="text-sm font-semibold text-base-content/80 mb-2">
                                      Detail Organisasi/Lembaga:
                                    </p>
                                    <ul className="space-y-1">
                                      {agreement.organizations.map(
                                        (org, orgIndex) => (
                                          <li
                                            key={orgIndex}
                                            className="text-xs text-base-content/60 leading-relaxed"
                                          >
                                            • {org}
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary Statistics */}
                  <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-bold text-base-content mb-4">
                      Ringkasan Statistik
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-primary">5</div>
                        <div className="text-sm text-base-content/70">
                          Benua
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-secondary">
                          35+
                        </div>
                        <div className="text-sm text-base-content/70">
                          Negara
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-success">
                          80+
                        </div>
                        <div className="text-sm text-base-content/70">
                          Masih Berlaku
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-warning">6</div>
                        <div className="text-sm text-base-content/70">
                          Proses
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-accent">
                          90+
                        </div>
                        <div className="text-sm text-base-content/70">
                          Total Perjanjian
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Key Focus Areas */}
                  <div className="bg-base-200 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-bold text-base-content mb-6">
                      Area Kerjasama Utama
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🚨</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Kejahatan Transnasional
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Pencegahan dan pemberantasan kejahatan lintas negara
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">💊</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Narkotika
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Pemberantasan perdagangan narkoba internasional
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">💻</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Kejahatan Siber
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Kerjasama penanggulangan cybercrime
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">👥</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Perdagangan Manusia
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Pencegahan trafficking dan eksploitasi
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🔒</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Counter Terrorism
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Pencegahan dan penanggulangan terorisme
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🎓</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Capacity Building
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Pengembangan kapasitas dan pelatihan
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerjanjianInternasional;
